import * as yup from 'yup';

function startDateValidation(formatMessage) {
  return yup.string().required(
    formatMessage({
      id: 'START_DATE_IS_REQUIRED',
      defaultMessage: 'Start Date is required',
    }),
  );
}

function endDate(formatMessage) {
  return yup
    .string()
    .nullable()
    .when(
      [
        'recurrenceEnabled',
        'recurrenceFrequency',
        'startDate',
        'recurringEndDateOptions',
      ],
      {
        is: (
          recurrenceEnabled,
          recurrenceFrequency,
          startDate,
          recurringEndDateOptions,
        ) => {
          // When non recurring and duration is a range
          if (recurrenceEnabled === false) {
            return recurrenceFrequency === 'RANGE' && startDate;
          }
          if (recurringEndDateOptions !== 'NO_END_DATE') {
            return true;
          }

          return false;
        },
        then: schema =>
          schema.required(
            formatMessage({
              id: 'END_DATE_IS_REQUIRED',
              defaultMessage: 'End Date is required',
            }),
          ),
        otherwise: schema => schema.notRequired(),
      },
    )
    .test(
      'end-date-after-start-date',
      formatMessage({
        id: 'END_DATE_MUST_BE_AFTER_START_DATE',
        defaultMessage: 'End Date must be after Start Date',
      }),
      (value, context) => {
        if (!value) {
          return true;
        }
        const { startDate: startDateValue } = context.parent;
        if (!startDateValue) {
          return true;
        }
        return new Date(value) > new Date(startDateValue);
      },
    );
}

function slotsValidation(formatMessage) {
  return yup.array().when(['slotAvailability'], {
    is: slotAvailability => {
      return slotAvailability === 'NOT_AVAILABLE_FOR_LIMITED_SLOTS';
    },
    then: schema =>
      schema
        .required(
          formatMessage({
            id: 'TIMESLOTS_ARE_REQUIRED',
            defaultMessage: 'Timeslots are required',
          }),
        )
        .min(
          1,
          formatMessage({
            id: 'TIMESLOTS_ARE_REQUIRED',
            defaultMessage: 'Timeslots are required',
          }),
        ),
    otherwise: schema => schema.notRequired(),
  });
}
function recurrenceWeekdays(formatMessage) {
  return yup.array().when(['recurrenceFrequency'], {
    is: recurrenceFrequency => {
      return recurrenceFrequency === 'WEEKLY';
    },
    then: schema =>
      schema
        .required(
          formatMessage({
            id: 'WEEKS_IS_REQUIRED',
            defaultMessage: 'Week(s) is required',
          }),
        )
        .min(
          1,
          formatMessage({
            id: 'WEEKS_IS_REQUIRED',
            defaultMessage: 'Week(s) is required',
          }),
        ),
    otherwise: schema => schema.notRequired(),
  });
}

function recurrenceMonthlyModeValidation(formatMessage) {
  return yup.mixed().test(
    'recurrenceMonthlyMode',
    formatMessage({
      id: 'MONTHLY_OPTIONS_IS_REQUIRED',
      defaultMessage: 'Monthly Options is required',
    }),
    function test(value) {
      const {
        recurrenceFrequency,
        recurrenceMonthlyMode1DOM,
        recurrenceMonthlyMode2OWO,
        recurrenceMonthlyMode2OWW,
        recurrenceMonthlyMode3O,
      } = this.parent;

      if (
        recurrenceFrequency === 'MONTHLY' &&
        value === 'DAY_OF_MONTH' &&
        !recurrenceMonthlyMode1DOM
      ) {
        return this.createError({
          message: formatMessage({
            id: 'DAY_OF_MONTH_IS_REQUIRED',
            defaultMessage: 'Day of Month is required',
          }),
        });
      }

      if (recurrenceFrequency === 'MONTHLY' && value === 'ORDINAL_WEEKDAY') {
        if (!recurrenceMonthlyMode2OWO && !recurrenceMonthlyMode2OWW) {
          return this.createError({
            message: formatMessage({
              id: 'ORDINAL_AND_MONTH_IS_REQUIRED',
              defaultMessage: 'Ordinal and Month is required',
            }),
          });
        }
        if (!recurrenceMonthlyMode2OWO) {
          return this.createError({
            message: formatMessage({
              id: 'ORDINAL_IS_REQUIRED',
              defaultMessage: 'Ordinal is required',
            }),
          });
        }
        if (!recurrenceMonthlyMode2OWW) {
          return this.createError({
            message: formatMessage({
              id: 'MONTH_IS_REQUIRED',
              defaultMessage: 'Month is required',
            }),
          });
        }
      }
      if (
        recurrenceFrequency === 'MONTHLY' &&
        value === 'ORDINAL_DAY' &&
        !recurrenceMonthlyMode3O
      ) {
        return this.createError({
          message: formatMessage({
            id: 'ORDINAL_IS_REQUIRED',
            defaultMessage: 'Ordinal is required',
          }),
        });
      }
      return true;
    },
  );
}

function recurrenceYearlyModeValidation(formatMessage) {
  return yup.mixed().test(
    'recurrenceYearlyMode',
    formatMessage({
      id: 'YEARLY_OPTIONS_IS_REQUIRED',
      defaultMessage: 'Yearly Options is required',
    }),
    function test(value) {
      const {
        recurrenceFrequency,
        recurrenceYearlyMode1MM,
        recurrenceYearlyMode1MD,
        recurrenceYearlyMode2OWMO,
        recurrenceYearlyMode2OWMW,
        recurrenceYearlyMode2OWMM,
      } = this.parent;

      if (recurrenceFrequency === 'YEARLY' && value === 'MONTH_DAY') {
        if (!recurrenceYearlyMode1MM && !recurrenceYearlyMode1MD) {
          return this.createError({
            message: formatMessage({
              id: 'MONTH_AND_DAY_IS_REQUIRED',
              defaultMessage: 'Month and Day is required',
            }),
          });
        }
        if (!recurrenceYearlyMode1MM) {
          return this.createError({
            message: formatMessage({
              id: 'MONTH_IS_REQUIRED',
              defaultMessage: 'Month is required',
            }),
          });
        }
        if (!recurrenceYearlyMode1MD) {
          return this.createError({
            message: formatMessage({
              id: 'DAY_IS_REQUIRED',
              defaultMessage: 'Day is required',
            }),
          });
        }
      }

      if (
        recurrenceFrequency === 'YEARLY' &&
        value === 'ORDINAL_WEEKDAY_IN_MONTH'
      ) {
        if (
          !recurrenceYearlyMode2OWMO &&
          !recurrenceYearlyMode2OWMW &&
          !recurrenceYearlyMode2OWMM
        ) {
          return this.createError({
            message: formatMessage({
              id: 'ORDINAL_WEEK_AND_MONTH_IS_REQUIRED',
              defaultMessage: 'Ordinal, Week and Month is required',
            }),
          });
        }
        if (!recurrenceYearlyMode2OWMO) {
          return this.createError({
            message: formatMessage({
              id: 'ORDINAL_IS_REQUIRED',
              defaultMessage: 'Ordinal is required',
            }),
          });
        }
        if (!recurrenceYearlyMode2OWMW) {
          return this.createError({
            message: formatMessage({
              id: 'WEEK_IS_REQUIRED',
              defaultMessage: 'Week is required',
            }),
          });
        }
        if (!recurrenceYearlyMode2OWMM) {
          return this.createError({
            message: formatMessage({
              id: 'MONTH_IS_REQUIRED',
              defaultMessage: 'Month is required',
            }),
          });
        }
      }

      return true;
    },
  );
}

function name(formatMessage) {
  return yup.string().required(
    formatMessage({
      id: 'NAME_IS_REQUIRED',
      defaultMessage: 'Name is required',
    }),
  );
}

const validationSchema = formatMessage =>
  yup
    .object({
      name: name(formatMessage),
    })
    .required();

export const nonSchemaValidations = formatMessage =>
  yup
    .object({
      startDate: startDateValidation(formatMessage),
      endDate: endDate(formatMessage),
      slots: slotsValidation(formatMessage),
      recurrenceWeekdays: recurrenceWeekdays(formatMessage),
      recurrenceMonthlyMode: recurrenceMonthlyModeValidation(formatMessage),
      recurrenceYearlyMode: recurrenceYearlyModeValidation(formatMessage),
    })
    .required();

export default validationSchema;
